// Monaco ReferenceProvider for navigational-features Phase 3 — Slices 24 + 25
// + 26, cross-object peek revision (2026-08-23, user decision).
//
// The provider now returns REAL Monaco `Location[]`. References to a database
// object live in OTHER persisted objects (view / routine / trigger /
// constraint bodies), which have no buffer in the workspace — so for each
// referencing object we synthesize a read-only `ITextModel` under a
// `jamsql-ref:` URI (`peekReferenceModels.ts`) and point the locations at it.
// Monaco standalone resolves peek locations straight out of the model
// registry, which makes "Peek References" / "Go to References" / "Find All
// References" (Shift+F12) render a genuine multi-file peek.
//
// Occurrences in the ACTIVE buffer are included as locations into the live
// model, so the peek reads as "here, plus everywhere in the database".
//
// The `ReferencesTab` is no longer an automatic side-effect (Monaco routes all
// three commands through this one provider, so a side-effect would fire on
// every peek). It stays reachable as an explicit editor action — see
// `findReferencesInTabAction.ts` — and from the Object-Explorer context menus.
//
// COLD CASE POLICY. Which tree node the cursor binds to depends on how the
// server compares names, and on a connection whose index has not been built
// that has not been probed yet — the OE walk falls back to case-INSENSITIVE,
// so in a `_CS_` database a typed `B.dbo.users` is satisfied by `B.DBO.users`
// too, and a spelling the catalog does not hold at all is satisfied by
// whatever the tree lists first. Peek then lists another object's references,
// or reports a false empty. This provider already waits for the target
// database's index before it queries, so it RE-RESOLVES the cursor behind
// that wait and searches for whatever binds under the policy the wait learnt
// (parity with `rename/renameProvider.ts`'s `openOncePolicyKnown`, which uses
// the cheaper policy-only gate because the preview does its own index wait).
// The guard that says whether the gesture is still the one being answered, the
// table of what each refusal reason means, and the bounded loop that re-verifies
// a binding the policy moved to another database are shared with the other two
// cursor surfaces — `invocationContext.ts`.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 24 / 25 / 26.

import type * as monacoNs from 'monaco-editor';

import { databaseEngineIdToSqlEngine } from '../../intellisense/versionGating/engineIdMapping';
import { indexReadyFor } from '../connection-lifecycle/navigationalLifecycle';
import { getMysqlLowerCaseTableNamesFor } from '../mysqlCaseProbe';
import { trackNavFeature } from '../analytics/trackNavFeature';

import { findReferences } from './findReferencesService';
import { findBufferReferenceRanges } from './bufferReferenceRanges';
import {
  buildPeekReferenceLocations,
  sqlRangeToMonacoRange,
  type ObjectBodyLoader,
} from './peekReferenceLocations';
import type { PeekModelMonacoApi } from './peekReferenceModels';
import {
  captureContext,
  contextHeldStill,
  resolveBehindPolicyWait,
  type VersionedModel,
} from './invocationContext';
import {
  findReferenceTargetAtCursor,
  type CursorModel,
  type ReferenceProviderDeps,
  type ReferenceTarget,
} from './referenceTargetAtCursor';

export type { ReferenceProviderDeps } from './referenceTargetAtCursor';

export type PeekReferenceProviderDeps = ReferenceProviderDeps & {
  /** Monaco namespace — injected so the module stays free of runtime monaco
   *  imports and unit tests can drive it with a fake registry. */
  readonly monaco: PeekModelMonacoApi;
  /** Language id the synthesized models are created under. Matches the host
   *  editor's language so peek previews get the same SQL colouring. */
  readonly getLanguageId: () => string;
  /** Test seam — overrides the per-engine object-body introspection feed. */
  readonly loadBody?: ObjectBodyLoader;
};

export function createReferenceProvider(
  deps: PeekReferenceProviderDeps,
): monacoNs.languages.ReferenceProvider {
  return {
    provideReferences: (model, position) => collectReferenceLocations(deps, model, position),
  };
}

/** Exported for unit tests: resolves the cursor target, queries the index,
 *  synthesizes the models and returns the peek locations. */
export async function collectReferenceLocations(
  deps: PeekReferenceProviderDeps,
  model: CursorModel & VersionedModel & { readonly uri: monacoNs.Uri },
  position: monacoNs.Position,
): Promise<monacoNs.languages.Location[]> {
  const cursorTarget = findReferenceTargetAtCursor(deps, model, position);
  if (!cursorTarget) return [];

  const engine = databaseEngineIdToSqlEngine(deps.engineId);

  // Wait for the EDITOR database's index — created and built on demand here,
  // whether or not the connect-time schedule for the configured database has
  // run — then RE-RESOLVE the cursor under the policy that wait learnt (see the
  // COLD CASE POLICY note at the top). A re-resolution that names ANOTHER
  // database is waited for and walked again, because that database's own
  // collation is exactly what this wait never probed; the loop is bounded and a
  // binding that never settles yields nothing.
  //
  // The three outcomes, identical to the ones F2 rename and the References tab
  // act on (`invocationContext.ts` holds the table and the argument):
  //
  //   - `'resolved'` — search that target's index.
  //   - `'unlearnable'` (`'no-lifecycle'`: a mock / unindexed engine, a
  //     connection that left the store) — there is no policy to learn and no
  //     index behind it, so the cursor's own binding stands and the index is
  //     not consulted at all. Reading it anyway can answer with a replacement
  //     lifecycle's still-building handle, and Monaco's peek widget has nowhere
  //     to say "partial" — it would list a subset of the references as if it
  //     were all of them (parity with `planRename`, which bails to `no-index`
  //     without reading `getIndex`; the References TAB does have somewhere to
  //     render the failure, and says so there). The ACTIVE BUFFER's own
  //     occurrences below need no index and are still listed.
  //   - `'abort'` — the invocation context moved, the wait refused for a reason
  //     that leaves the binding unverified, the learnt policy ruled the typed
  //     spelling out, or the database never settled. Nothing is listed.
  const captured = captureContext(deps, model, cursorTarget.connectionId);
  const heldStill = () => contextHeldStill(deps, model, captured);
  const resolution = await resolveBehindPolicyWait<ReferenceTarget>({
    cursorTarget,
    awaitPolicy: (t) => indexReadyFor(t.connectionId, t.database),
    resolve: () => findReferenceTargetAtCursor(deps, model, position),
    heldStill,
  });
  if (resolution.kind === 'abort') return [];
  const target = resolution.target;
  const indexReady = resolution.kind === 'resolved';

  // Read AFTER the wait: a cold MySQL build is what learns
  // `@@lower_case_table_names` (see `mysqlCaseProbe.ts`).
  const mysqlLowerCaseTableNames = getMysqlLowerCaseTableNamesFor(
    target.connectionId,
    deps.engineId,
  );

  const groups = indexReady
    ? findReferences(target.connectionId, target.identifier, target.identifierKind, {
        database: target.database,
        engine,
        mysqlLowerCaseTableNames,
      }).groups
    : [];

  // The index wait was only the FIRST await. Every referencing object's body is
  // now fetched over IPC — one round trip per group — and the `jamsql-ref:`
  // models are minted from those bodies, so the invocation context has a second
  // and longer window to move under the gesture. The same guard therefore rides
  // into the loop (it stops loading and mints no model once the context moved)
  // and is asked once more here, because the move can land between the loop's
  // last check and this line.
  const locations = await buildPeekReferenceLocations({
    monaco: deps.monaco,
    languageId: deps.getLanguageId(),
    connectionId: target.connectionId,
    database: target.database,
    engineId: deps.engineId,
    groups,
    loadBody: deps.loadBody,
    isCancelled: () => !heldStill(),
  });

  // A context that moved is answered with NOTHING — not even the active
  // buffer's own occurrences, which is exactly what a context that moved under
  // the index wait already produces (`'abort'` above): the identifier those
  // occurrences would be matched against was bound in a context the user has
  // left, and the buffer they would point into may not even be the one the
  // gesture fired in. One keystroke re-fires the peek in the new context.
  if (!heldStill()) return [];

  for (const range of findBufferReferenceRanges({
    source: model.getValue(),
    engine: engine ?? 'mssql',
    identifier: target.identifier,
    kind: target.identifierKind,
    mysqlLowerCaseTableNames,
  })) {
    locations.push({ uri: model.uri, range: sqlRangeToMonacoRange(range) });
  }

  trackNavFeature({
    name: 'feature:navfeatures_peek_references',
    props: { kind: target.identifierKind, hasResults: locations.length > 0 },
  });

  return locations;
}
